import React from 'react';
import { FlexDiv, TextDefault, TextInput } from './ui';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    ensureGroupOfBlocksActions,
    getGroupChildOptionComponent,
    getGroupChildOptionScore,
    getOptionActionKey,
    removeGroupChildAction,
    setGroupChildOptionComponent,
    setGroupChildValuesScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
};

const INNER_TYPES = ['radio', 'checkbox', 'dropDown', 'numericalscale'];

function getGroup(block) {
    return block.group || block.blocks || [];
}

/**
 * Group of blocks: `block.group[]` with per-child `actionId` and nested
 * `actions[parentActionId][childActionId].values`.
 */
export function GroupOfBlocksBlockEditor({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    if (selectedBlock?.type !== 'groupOfBlocks') {
        return null;
    }

    const group = getGroup(selectedBlock);
    const parentId = selectedBlock.actionId;
    const answer = selectedBlock.answer || {};
    const heading = Array.isArray(answer.heading) ? answer.heading : [];

    const setGroup = (nextGroup) => {
        onUpdateBlock({ group: nextGroup, blocks: undefined });
        if (onPatchQuestion) {
            onPatchQuestion((prev) => ({
                ...prev,
                actions: ensureGroupOfBlocksActions(
                    prev.actions,
                    { ...selectedBlock, group: nextGroup },
                    prev
                ),
            }));
        }
    };

    const patchChild = (index, patch) => {
        const next = group.map((g, i) => (i === index ? { ...g, ...patch } : g));
        setGroup(next);
    };

    const patchChildOption = (childIndex, optIndex, patch) => {
        const child = group[childIndex];
        const opts = [...(child.options || [])];
        const cur = opts[optIndex];
        opts[optIndex] =
            typeof cur === 'string'
                ? { text: patch.text ?? cur, value: patch.value ?? cur }
                : { ...cur, ...patch };
        patchChild(childIndex, { options: opts });
    };

    const addChild = () => {
        const id = `grp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        setGroup([
            ...group,
            {
                type: 'radio',
                title: 'New question',
                actionId: id,
                options: [
                    { text: 'Yes', value: 'yes' },
                    { text: 'No', value: 'no' },
                ],
            },
        ]);
    };

    const removeChild = (index) => {
        const child = group[index];
        const next = group.filter((_, i) => i !== index);
        setGroup(next);
        if (onPatchQuestion && parentId && child?.actionId) {
            onPatchQuestion((prev) => ({
                ...prev,
                actions: removeGroupChildAction(
                    prev.actions,
                    parentId,
                    child.actionId
                ),
            }));
        }
    };

    const addChildOption = (childIndex) => {
        const child = group[childIndex];
        const opts = [...(child.options || [])];
        const n = opts.length + 1;
        opts.push({ text: `Option ${n}`, value: `opt_${Date.now()}` });
        patchChild(childIndex, { options: opts });
    };

    const removeChildOption = (childIndex, optIndex) => {
        const child = group[childIndex];
        const opts = [...(child.options || [])];
        if (opts.length <= 2) return;
        opts.splice(optIndex, 1);
        patchChild(childIndex, { options: opts });
    };

    const patchAnswer = (patch) => {
        onUpdateBlock({ answer: { ...answer, ...patch } });
    };

    return (
        <FlexDiv column gap={2}>
            <TextDefault bold>Group of blocks</TextDefault>
            <TextInput
                label="Variant"
                value={selectedBlock.variant || ''}
                onChange={(e) => onUpdateBlock({ variant: e.target.value })}
                placeholder="e.g. updates-center"
            />
            <TextInput
                label="Pretitle"
                value={selectedBlock.pretitle || ''}
                onChange={(e) => onUpdateBlock({ pretitle: e.target.value })}
            />
            <TextInput
                label="Description"
                value={selectedBlock.description || ''}
                onChange={(e) => onUpdateBlock({ description: e.target.value })}
            />

            {group.map((child, childIndex) => (
                <Paper key={child.actionId || childIndex} elevation={0} sx={paperSx}>
                    <Stack spacing={1.5}>
                        <FlexDiv alignCenter gap={8}>
                            <TextDefault bold>
                                Inner block {childIndex + 1}
                            </TextDefault>
                            {group.length > 1 ? (
                                <IconButton
                                    size="small"
                                    aria-label="Remove inner block"
                                    onClick={() => removeChild(childIndex)}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </FlexDiv>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={child.type || 'radio'}
                                onChange={(e) =>
                                    patchChild(childIndex, {
                                        type: e.target.value,
                                    })
                                }
                            >
                                {INNER_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Inner title"
                            value={child.title || ''}
                            onChange={(e) =>
                                patchChild(childIndex, { title: e.target.value })
                            }
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Child actionId"
                            value={child.actionId || ''}
                            onChange={(e) =>
                                patchChild(childIndex, {
                                    actionId: e.target.value,
                                })
                            }
                            fullWidth
                        />
                        <TextDefault bold>Options & scores</TextDefault>
                        {(child.options || []).map((opt, optIndex) => {
                            const key = getOptionActionKey(opt, optIndex);
                            const score =
                                parentId && child.actionId
                                    ? getGroupChildOptionScore(
                                          question.actions,
                                          parentId,
                                          child.actionId,
                                          key
                                      )
                                    : 0;
                            const comp =
                                parentId &&
                                child.actionId &&
                                onPatchQuestion
                                    ? getGroupChildOptionComponent(
                                          question.actions,
                                          parentId,
                                          child.actionId,
                                          key,
                                          question
                                      )
                                    : 'behaviour';
                            return (
                                <Paper
                                    key={optIndex}
                                    variant="outlined"
                                    sx={{ p: 1.5, bgcolor: 'background.paper' }}
                                >
                                    <Stack spacing={1}>
                                        <FlexDiv gap={8} wrap>
                                            <TextField
                                                size="small"
                                                label="Label"
                                                value={
                                                    typeof opt === 'string'
                                                        ? opt
                                                        : opt?.text ?? ''
                                                }
                                                onChange={(e) =>
                                                    patchChildOption(
                                                        childIndex,
                                                        optIndex,
                                                        { text: e.target.value }
                                                    )
                                                }
                                                sx={{ flex: 1, minWidth: 100 }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Value"
                                                value={
                                                    typeof opt === 'string'
                                                        ? opt
                                                        : opt?.value ?? ''
                                                }
                                                onChange={(e) =>
                                                    patchChildOption(
                                                        childIndex,
                                                        optIndex,
                                                        { value: e.target.value }
                                                    )
                                                }
                                                sx={{ width: 160 }}
                                            />
                                        </FlexDiv>
                                        <FlexDiv gap={8} wrap>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Score"
                                                value={score}
                                                onChange={(e) => {
                                                    if (
                                                        !onPatchQuestion ||
                                                        !parentId ||
                                                        !child.actionId
                                                    )
                                                        return;
                                                    onPatchQuestion((prev) => ({
                                                        ...prev,
                                                        actions:
                                                            setGroupChildValuesScore(
                                                                prev.actions,
                                                                parentId,
                                                                child.actionId,
                                                                key,
                                                                e.target.value,
                                                                prev
                                                            ),
                                                    }));
                                                }}
                                                sx={{ width: 120 }}
                                            />
                                            {onPatchQuestion ? (
                                                <ScoringComponentSelect
                                                    componentOptions={
                                                        scoringComponentOptions
                                                    }
                                                    value={comp}
                                                    onChange={(next) =>
                                                        onPatchQuestion((prev) => ({
                                                            ...prev,
                                                            actions:
                                                                setGroupChildOptionComponent(
                                                                    prev.actions,
                                                                    parentId,
                                                                    child.actionId,
                                                                    key,
                                                                    next,
                                                                    prev
                                                                ),
                                                        }))
                                                    }
                                                />
                                            ) : null}
                                        </FlexDiv>
                                        {(child.options || []).length > 2 ? (
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    removeChildOption(
                                                        childIndex,
                                                        optIndex
                                                    )
                                                }
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        ) : null}
                                    </Stack>
                                </Paper>
                            );
                        })}
                        <FlexDiv>
                            <IconButton
                                size="small"
                                onClick={() => addChildOption(childIndex)}
                            >
                                <AddIcon />
                            </IconButton>
                            <TextDefault lighter>Add option</TextDefault>
                        </FlexDiv>
                    </Stack>
                </Paper>
            ))}
            <FlexDiv>
                <IconButton size="small" onClick={addChild} aria-label="Add inner block">
                    <AddIcon />
                </IconButton>
                <TextDefault lighter>Add inner block</TextDefault>
            </FlexDiv>

            <TextDefault bold>Group answer (review)</TextDefault>
            <TextField
                size="small"
                label="Answer description (i18n key or text)"
                value={answer.description || ''}
                onChange={(e) =>
                    patchAnswer({ description: e.target.value })
                }
                fullWidth
            />
            <TextField
                size="small"
                label="Review heading text"
                value={answer.reviewHeadingText || ''}
                onChange={(e) =>
                    patchAnswer({ reviewHeadingText: e.target.value })
                }
                fullWidth
                multiline
                minRows={2}
            />
            {heading.map((row, index) => (
                <Paper key={index} elevation={0} sx={paperSx}>
                    <Stack spacing={1}>
                        <TextField
                            size="small"
                            label="Heading text"
                            value={row.text ?? ''}
                            onChange={(e) => {
                                const next = heading.map((h, i) =>
                                    i === index ? { ...h, text: e.target.value } : h
                                );
                                patchAnswer({ heading: next });
                            }}
                            fullWidth
                        />
                        <FlexDiv gap={8}>
                            <TextField
                                size="small"
                                type="number"
                                label="Score from"
                                value={row.scoreFrom ?? ''}
                                onChange={(e) => {
                                    const next = heading.map((h, i) =>
                                        i === index
                                            ? {
                                                  ...h,
                                                  scoreFrom: Number(e.target.value),
                                              }
                                            : h
                                    );
                                    patchAnswer({ heading: next });
                                }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Score to"
                                value={row.scoreTo ?? ''}
                                onChange={(e) => {
                                    const next = heading.map((h, i) =>
                                        i === index
                                            ? {
                                                  ...h,
                                                  scoreTo: Number(e.target.value),
                                              }
                                            : h
                                    );
                                    patchAnswer({ heading: next });
                                }}
                            />
                        </FlexDiv>
                    </Stack>
                </Paper>
            ))}
        </FlexDiv>
    );
}
